// FAQSection.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'Do you guarantee jobs?',
      answer: 'No. We do not guarantee employment. We provide access to verified interview opportunities for eligible candidates through our established employer partnerships. Our role is to prepare strong applications, coordinate interview opportunities, and support you throughout the process. However, all hiring decisions are made solely by employers based on their own criteria and standards. We focus on ensuring you are well-prepared and connected to legitimate opportunities, but the final employment decision rests with the employer.'
    },
    {
      question: 'Is the interview guaranteed?',
      answer: 'Eligible candidates who are placed in our Work Abroad pipeline may be offered guaranteed interview slots with partner employers, subject to meeting specific role requirements and employer criteria. The availability of interview slots depends on current employer demand and your readiness as a candidate. While we work to secure interview opportunities for qualified candidates in our pipeline, the timing and availability are influenced by employer hiring cycles and needs. Interview opportunities do not guarantee job offers, as employers make final hiring decisions based on interview performance and their selection criteria.'
    },
    {
      question: 'What roles do you support?',
      answer: 'We primarily support care and healthcare support worker roles, as well as selected skilled and semi-skilled positions across various industries. The specific roles available depend on current employer demand, destination country requirements, and candidate profiles. During your eligibility consultation, we will discuss the roles that align with your experience, qualifications, and career goals. We match candidates with positions where they have the strongest chance of success based on their background and the current opportunities available through our employer partnerships.'
    },
    {
      question: 'Is there a fee for this service?',
      answer: 'Yes. A service fee applies for candidates who proceed into the Work Abroad pipeline after the initial eligibility assessment. This fee is charged at the point of pipeline placement and covers the comprehensive support services we provide, including eligibility screening, role matching, interview coordination with employers, preparation materials and coaching, documentation guidance, and ongoing support throughout the hiring process. The initial eligibility consultation is designed to provide clarity on your options before any financial commitment is required. We will discuss all fees transparently during your consultation.'
    },
    {
      question: 'Who pays who?',
      answer: 'Candidates do not pay Notapaths for employment, salaries, or any employer-related fees. Our service fee covers professional support services only, including preparation, coordination, and guidance throughout the process. Any employment contracts, salary payments, visa fees, or other employer-related payments are handled directly between you and the employer or relevant authorities. We do not act as an intermediary for employment payments. All financial arrangements related to your actual employment are between you and your employer, ensuring complete transparency and security in your employment relationship.'
    },
    {
      question: 'What happens if I fail the interview?',
      answer: 'If you are unsuccessful in an interview, we will review the outcome with you to understand what happened and provide constructive feedback where possible. Depending on the circumstances, your profile, and current employer availability, we may explore additional opportunities for you. However, this is assessed on a case-by-case basis and depends on factors such as employer feedback, your readiness for other roles, and available positions at that time. We remain committed to supporting candidates throughout their journey, but we also provide honest guidance about realistic next steps based on each situation.'
    },
    {
      question: 'Do you handle visas?',
      answer: 'We provide guidance and preparation support related to employment documentation and visa requirements as part of our comprehensive service. This includes helping you understand what documents are needed, preparing required materials, and advising on the application process. However, final visa decisions and processing are handled by relevant immigration authorities and government agencies. We cannot influence or guarantee visa outcomes, as these decisions are made by official bodies based on their own criteria and regulations. Our role is to ensure you are well-prepared and informed throughout the documentation and visa application process.'
    },
    {
      question: 'Will I communicate directly with the employer?',
      answer: 'Yes. Once interviews are scheduled and the recruitment process begins, all formal employment communication including interview invitations, job offers, contract negotiations, salary discussions, and onboarding instructions will be directly between you and the employer. You will receive all official employment documents and correspondence in your name from the employer. While this direct communication is taking place, we remain available to guide you, help you understand any correspondence, advise on appropriate responses, and support you throughout the process. We act as your advisor and support system, not as an intermediary for official employment communication.'
    },
    {
      question: 'What if I am unsure about my eligibility?',
      answer: 'The eligibility consultation is specifically designed to address this concern. During this free initial session, we thoroughly review your work experience, qualifications, relevant certifications, documentation readiness, and any other factors that may impact your eligibility for international employment. We then provide you with honest, realistic feedback about your chances of success, which roles might be suitable for you, and what steps you may need to take to strengthen your profile if necessary. Our goal is to give you a clear, truthful assessment so you can make informed decisions about pursuing work abroad opportunities without any surprises later in the process. There is no obligation to proceed after the consultation.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            Clear answers to common questions about our Work Abroad services
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="border border-gray-200 rounded-lg overflow-hidden bg-white"
            >
              {/* Question Button */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-[#F5F8FA] transition-colors"
              >
                <span className="text-base font-semibold text-[#202356] pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={20} className="text-[#202356]" />
                  ) : (
                    <Plus size={20} className="text-[#202356]" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0 bg-white">
                      <p className="text-gray-600 text-sm font-light leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
